import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const EventBudget = () => {
   // State variables for the event budget calculation
   const [venueCost, setVenueCost] = useState(0);
   const [cateringCost, setCateringCost] = useState(0);
   const [entertainmentCost, setEntertainmentCost] = useState(0);
   const [marketingCost, setMarketingCost] = useState(0);
   const [otherCosts, setOtherCosts] = useState(0);
   const [totalEventCost, setTotalEventCost] = useState(0);

   // Event handlers to update state values and ensure the inputs are valid numbers
   const handleVenueCostChange = (e) => {
     setVenueCost(parseFloat(e.target.value) || 0);
   };
   const handleCateringCostChange = (e) => {
     setCateringCost(parseFloat(e.target.value) || 0);
   };
   const handleEntertainmentCostChange = (e) => {
     setEntertainmentCost(parseFloat(e.target.value) || 0);
   };
   const handleMarketingCostChange = (e) => {
     setMarketingCost(parseFloat(e.target.value) || 0);
   };
   const handleOtherCostsChange = (e) => {
     setOtherCosts(parseFloat(e.target.value) || 0);
   };

   // useEffect hook to calculate total event cost when any cost category changes
   useEffect(() => {
     const totalCost =
       (isNaN(venueCost) ? 0 : venueCost) +
       (isNaN(cateringCost) ? 0 : cateringCost) +
       (isNaN(entertainmentCost) ? 0 : entertainmentCost) +
       (isNaN(marketingCost) ? 0 : marketingCost) +
       (isNaN(otherCosts) ? 0 : otherCosts);

     setTotalEventCost(totalCost);

     // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

   }, [venueCost, cateringCost, entertainmentCost, marketingCost, otherCosts]);

  return (
    <Layout title="Event Budget Calculator" path="event-budget">
        <section className="section-padding" id="event-budget-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Event Budget Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Event Budget Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Venue Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="venueCost" className="form-label">
                        Venue Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Venue Cost:</strong> Enter the cost of renting the venue for your event."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="venueCost"
                        placeholder="Enter venue cost"
                        value={venueCost || ""}
                        onChange={handleVenueCostChange}
                      />
                    </div>

                    {/* Catering Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="cateringCost" className="form-label">
                        Catering Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Catering Cost:</strong> Enter the total cost for food and beverages."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="cateringCost"
                        placeholder="Enter catering cost"
                        value={cateringCost || ""}
                        onChange={handleCateringCostChange}
                      />
                    </div>

                    {/* Entertainment Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="entertainmentCost" className="form-label">
                        Entertainment Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Entertainment Cost:</strong> Include the cost for performers, music, and other entertainment services."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="entertainmentCost"
                        placeholder="Enter entertainment cost"
                        value={entertainmentCost || ""}
                        onChange={handleEntertainmentCostChange}
                      />
                    </div>

                    {/* Marketing Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="marketingCost" className="form-label">
                        Marketing Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Marketing Cost:</strong> Add the cost of promoting your event, such as ads or flyers."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="marketingCost"
                        placeholder="Enter marketing cost"
                        value={marketingCost || ""}
                        onChange={handleMarketingCostChange}
                      />
                    </div>

                    {/* Other Costs Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="otherCosts" className="form-label">
                        Other Costs (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Other Costs:</strong> Enter any additional expenses for your event."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="otherCosts"
                        placeholder="Enter other costs"
                        value={otherCosts || ""}
                        onChange={handleOtherCostsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Event Cost:{" "}
                        <span className="fw-bold ms-3">
                          {totalEventCost === 0 ? "0.00" : totalEventCost.toFixed(2)} ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
        </section>
      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Event Budget Calculator
                </h3>

                {/* What is an Event Budget Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is an Event Budget Calculator?
                </h4>
                <p>
                  An **Event Budget Calculator** helps event planners estimate
                  the total cost of organizing an event. It factors in various
                  cost categories, such as venue, catering, entertainment,
                  marketing, and other expenses, to help ensure the event stays
                  within budget.
                </p>

                {/* Why Use an Event Budget Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use an Event Budget Calculator?
                </h4>
                <p>
                  This calculator helps event planners stay within budget by
                  estimating all the necessary costs associated with organizing
                  an event. It provides a clear view of where money is being
                  spent and helps prevent overspending.
                </p>

                {/* Benefits of Using an Event Budget Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using an Event Budget Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Helps Event Planners Stay Within Budget:</strong>{" "}
                    Provides an accurate breakdown of all event-related costs to
                    prevent overspending.
                  </li>
                  <li>
                    <strong>Reduces the Risk of Overspending:</strong> Gives you
                    a clear overview of estimated costs, making it easier to
                    manage finances.
                  </li>
                  <li>
                    <strong>Ensures Efficient Resource Allocation:</strong>{" "}
                    Helps allocate funds effectively across different aspects of
                    the event, ensuring all important elements are covered.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use an Event Budget Calculator?
                </h4>
                <p>
                  The event budget calculator is ideal for anyone organizing an
                  event, including:
                </p>
                <ul>
                  <li>
                    <strong>Event Planners:</strong> Helps professional event
                    planners organize budgets for corporate and personal events.
                  </li>
                  <li>
                    <strong>Corporate Events:</strong> Useful for planning
                    conferences, seminars, and company parties, ensuring that
                    all costs are accounted for.
                  </li>
                  <li>
                    <strong>Personal Use:</strong> Perfect for organizing
                    weddings, birthdays, or any personal event to ensure costs
                    are controlled.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How do I calculate the event budget?
                </h4>
                <p>
                  To calculate the event budget, simply add the costs of all key
                  categories such as venue, catering, entertainment, marketing,
                  and other associated costs. The result will be the total
                  estimated event cost.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is it important to calculate an event budget?
                </h4>
                <p>
                  Having a well-calculated event budget ensures that you have
                  the necessary funds to cover all aspects of the event and can
                  help avoid unexpected financial problems during the planning
                  process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EventBudget;
